import { Icon } from '@iconify/react';
import { forwardRef, useRef } from 'react';
import CustomSelect from './CustomSelect.jsx';
import './formSection.css';

function TextareaWithMobileGrip({
  id,
  className = '',
  required,
  placeholder,
  value,
  'aria-invalid': ariaInvalid,
  onChange,
  onInput,
}) {
  const ref = useRef(null);

  const onGripPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;

    const startY = e.clientY;
    const startH = el.getBoundingClientRect().height;
    const maxH = Math.round(window.innerHeight * 0.7);
    let frame = 0;
    let latestY = startY;

    const apply = () => {
      frame = 0;
      const next = Math.max(100, Math.min(maxH, startH + (latestY - startY)));
      el.style.height = `${next}px`;
    };

    const onMove = (ev) => {
      latestY = ev.clientY;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onUp = () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div className="form-textarea-wrap">
      <textarea
        ref={ref}
        id={id}
        className={className}
        required={required}
        placeholder={placeholder}
        value={value ?? ''}
        aria-invalid={ariaInvalid}
        onChange={(e) => {
          if (onInput) onInput(e);
          else onChange?.(e);
        }}
      />
      <button
        type="button"
        className="form-textarea-grip"
        aria-label="Змінити висоту поля"
        onPointerDown={onGripPointerDown}
      />
    </div>
  );
}

export function FormSection({
  icon,
  title,
  description,
  fields,
  values = {},
  radios = {},
  errors = {},
  onChange,
  onRadioChange,
  children,
  className = '',
  divider = true,
}) {
  const ctx = { values, radios };
  const allFields = Array.isArray(fields) ? fields : [];
  const honeypots = allFields.filter((f) => f.hidden);
  const visibleFields = allFields.filter(
    (f) => !f.hidden && (!f.showWhen || f.showWhen(ctx)),
  );

  // Layout rows from fields[].width:
  // - omit / 'full'  → 1 field per row
  // - 'half'         → 2 fields per row (pair consecutive halfs)
  // - 'third'        → 3 fields per row (group consecutive thirds)
  const rows = [];
  for (let i = 0; i < visibleFields.length; i += 1) {
    const field = visibleFields[i];
    const next = visibleFields[i + 1];
    const next2 = visibleFields[i + 2];
    if (
      field.width === 'third' &&
      next?.width === 'third' &&
      next2?.width === 'third'
    ) {
      rows.push({ fields: [field, next, next2], stackOnMobile: false });
      i += 2;
    } else if (field.width === 'half' && next?.width === 'half') {
      rows.push({ fields: [field, next], stackOnMobile: Boolean(field.stackOnMobile || next.stackOnMobile) });
      i += 1;
    } else {
      rows.push({ fields: [field], stackOnMobile: false });
    }
  }

  return (
    <section className={`form-section${divider ? ' form-section--divider' : ''} ${className}`.trim()}>
      <header className="form-section-header">
        {icon ? (
          // <span className="form-section-icon" aria-hidden>
            <Icon icon={icon} className="form-section-icon-svg" />
          // </span>
        ) : null}
        <h2 className="form-section-title">{title}</h2>
        {description ? <p className="form-section-desc">{description}</p> : null}
      </header>

      {rows.length > 0 ? (
        <div className="form-section-body">
          {rows.map((row, rowIdx) => {
            const rowFields = row.fields;
            const stackClass = row.stackOnMobile ? ' form-section-row--stack-mobile' : '';
            return (
            <div
              key={rowFields.map((f) => f.name).join('-') || rowIdx}
              className={
                rowFields.length === 3
                  ? `form-section-row form-section-row--3${stackClass}`
                  : rowFields.length === 2
                    ? `form-section-row form-section-row--2${stackClass}`
                    : 'form-section-row'
              }
            >
              {rowFields.map((field) => (
                <FormField
                  key={field.name}
                  field={field}
                  value={field.type === 'radio' ? radios[field.name] : values[field.name]}
                  invalid={Boolean(errors[field.name])}
                  onChange={onChange}
                  onRadioChange={onRadioChange}
                />
              ))}
            </div>
            );
          })}
        </div>
      ) : null}

      {honeypots.map((field) => (
        <div key={field.name} className="form-group" style={{ display: 'none' }} aria-hidden>
          <input
            type="text"
            id={field.name}
            name={field.name}
            className="form-input"
            tabIndex={field.tabIndex ?? -1}
            autoComplete={field.autoComplete || 'off'}
            value={values[field.name] ?? ''}
            onChange={(e) => onChange?.(field.name, e.target.value)}
          />
        </div>
      ))}

      {children ? <div className="form-section-extra">{children}</div> : null}
    </section>
  );
}

export const FormField = forwardRef(function FormField(
  { field, value, invalid = false, onChange, onRadioChange },
  ref,
) {
  const {
    name,
    label,
    type = 'text',
    required = false,
    placeholder,
    hint,
    error,
    options = [],
    min,
    max,
    step,
    inputRef,
    onInput,
    onBlur,
    autoComplete,
    tabIndex,
    footer,
  } = field;

  const id = name;
  const resolvedRef = inputRef || ref;
  const showError = Boolean(invalid || error);
  const controlClass = showError ? ' input-error' : '';

  const labelNode = label ? (
    <label className="form-label" htmlFor={type === 'radio' || type === 'checkboxes' ? undefined : id}>
      <span>
        {label}
        {required ? <span className="form-required" aria-hidden> *</span> : null}
      </span>
    </label>
  ) : null;

  const meta = (
    <>
      {hint ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
      {footer || null}
    </>
  );

  if (type === 'radio') {
    const compact = options.length > 0 && options.length <= 3;
    const activeIndex = Math.max(
      0,
      options.findIndex((opt) => opt.value === value),
    );
    const hasValue = options.some((opt) => opt.value === value);

    return (
      <div className={`form-group${showError ? ' form-group--error' : ''}`}>
        {labelNode}
        <div
          className={`form-toggle-group${compact ? ' form-toggle-group--compact' : ''}${showError ? ' is-error' : ''}`}
          role="radiogroup"
          aria-label={label}
          aria-invalid={showError || undefined}
          style={
            compact
              ? {
                  '--toggle-count': options.length,
                  '--toggle-index': hasValue ? activeIndex : 0,
                  '--toggle-thumb-opacity': hasValue ? 1 : 0,
                }
              : undefined
          }
        >
          {compact ? <span className="form-toggle-thumb" aria-hidden /> : null}
          {options.map((opt) => {
            const checked = value === opt.value;
            return (
              <label
                key={opt.value}
                className={`form-toggle${checked ? ' is-active' : ''}`}
              >
                <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  className="form-toggle-input"
                  checked={checked}
                  onChange={() => onRadioChange?.(name, opt.value)}
                />
                <span className="form-toggle-label">{opt.label}</span>
              </label>
            );
          })}
        </div>
        {meta}
      </div>
    );
  }

  if (type === 'checkboxes') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className={`form-group${showError ? ' form-group--error' : ''}`}>
        {labelNode}
        <div
          className={`form-check-cards${showError ? ' is-error' : ''}`}
          role="group"
          aria-label={label}
          aria-invalid={showError || undefined}
        >
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`form-check-card${checked ? ' is-active' : ''}`}
              >
                <input
                  type="checkbox"
                  className="form-check-card-input"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value];
                    onChange?.(name, next);
                  }}
                />
                <span className="form-check-card-mark" aria-hidden>
                  <svg viewBox="0 0 16 16" width="11" height="11" fill="none">
                    <path
                      d="M3.5 8.2 6.4 11l6.1-6.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {opt.icon ? (
                  <span className="form-check-card-icon" aria-hidden>
                    <Icon icon={opt.icon} width={18} height={18} />
                  </span>
                ) : null}
                <span className="form-check-card-label">{opt.label}</span>
              </label>
            );
          })}
        </div>
        {meta}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className={`form-group${showError ? ' form-group--error' : ''}`}>
        {labelNode}
        <CustomSelect
          id={id}
          value={value ?? ''}
          options={options}
          required={required}
          invalid={showError}
          aria-label={label}
          onChange={(next) => onChange?.(name, next)}
        />
        {meta}
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <div className={`form-group${showError ? ' form-group--error' : ''}`}>
        {labelNode}
        <TextareaWithMobileGrip
          id={id}
          className={`form-textarea${controlClass}`}
          required={required}
          placeholder={placeholder}
          value={value ?? ''}
          aria-invalid={showError || undefined}
          onChange={onInput ? undefined : (e) => onChange?.(name, e.target.value)}
          onInput={
            onInput
              ? (e) => {
                  onInput(e);
                  onChange?.(name, e.target.value);
                }
              : undefined
          }
        />
        {meta}
      </div>
    );
  }

  return (
    <div className={`form-group${showError ? ' form-group--error' : ''}`}>
      {labelNode}
      <input
        ref={resolvedRef}
        type={type}
        id={id}
        className={`form-input${controlClass}`}
        required={required}
        placeholder={placeholder}
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete}
        tabIndex={tabIndex}
        aria-invalid={showError || undefined}
        onChange={onInput ? undefined : (e) => onChange?.(name, e.target.value)}
        onInput={
          onInput
            ? (e) => {
                onInput(e);
                onChange?.(name, e.target.value);
              }
            : undefined
        }
        onBlur={onBlur}
      />
      {meta}
    </div>
  );
});

export default FormSection;
